import type { Geofence } from '@rabbitfuel/shared';

/**
 * Die Strecke als Linie fuer die Streckenkarte -- ohne Kartenkacheln.
 *
 * Die Punkte kommen aus der Strecken-Zone des Events (GPX-Import in den
 * Einstellungen). Projiziert wird plattkartenartig mit dem Kosinus der
 * mittleren Breite: fuer eine Runde von ein paar Kilometern ist das auf
 * Zentimeter so genau wie jede echte Projektion, und Entfernungen in diesen
 * Koordinaten sind verhaeltnisgleich zu Metern. Ein Anteil der Runde laesst
 * sich deshalb direkt entlang der Linie abtragen.
 */
export interface CourseShape {
  /** Punkte in Bildkoordinaten, die laengere Seite misst `COURSE_SPAN`. */
  xy: [number, number][];
  /** Laenge der Linie bis zu jedem Punkt; der letzte Wert ist die ganze Runde. */
  cum: number[];
  total: number;
  width: number;
  height: number;
}

export const COURSE_SPAN = 100;

/** Die Strecke eines Events -- nur eine Strecken-Zone hat eine. */
export const courseOf = (geofence: Geofence | null | undefined): [number, number][] | null =>
  geofence?.mode === 'route' && geofence.points.length >= 2 ? geofence.points : null;

export function courseShape(points: [number, number][] | null): CourseShape | null {
  if (!points || points.length < 2) return null;
  const lat0 = points.reduce((sum, p) => sum + p[0], 0) / points.length;
  const k = Math.cos((lat0 * Math.PI) / 180);
  // Norden oben: die Breite waechst nach oben, die y-Achse im Bild nach unten.
  const raw = points.map(([lat, lng]) => [lng * k, -lat] as [number, number]);
  const xs = raw.map((p) => p[0]);
  const ys = raw.map((p) => p[1]);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const spanX = Math.max(...xs) - minX;
  const spanY = Math.max(...ys) - minY;
  const span = Math.max(spanX, spanY);
  if (!(span > 0)) return null;
  const scale = COURSE_SPAN / span;
  const xy = raw.map(([x, y]) => [(x - minX) * scale, (y - minY) * scale] as [number, number]);
  const cum = [0];
  for (let i = 1; i < xy.length; i++) {
    cum.push(cum[i - 1] + Math.hypot(xy[i][0] - xy[i - 1][0], xy[i][1] - xy[i - 1][1]));
  }
  const total = cum[cum.length - 1];
  if (!(total > 0)) return null;
  return { xy, cum, total, width: spanX * scale, height: spanY * scale };
}

/** Stelle und Laufrichtung (Bogenmass) beim Anteil `fraction` der Runde. */
export function pointAt(shape: CourseShape, fraction: number): { x: number; y: number; angle: number } {
  const target = Math.min(1, Math.max(0, fraction)) * shape.total;
  let i = 1;
  while (i < shape.cum.length - 1 && shape.cum[i] < target) i++;
  const [ax, ay] = shape.xy[i - 1];
  const [bx, by] = shape.xy[i];
  const len = shape.cum[i] - shape.cum[i - 1];
  const t = len > 0 ? (target - shape.cum[i - 1]) / len : 0;
  return { x: ax + (bx - ax) * t, y: ay + (by - ay) * t, angle: Math.atan2(by - ay, bx - ax) };
}

/** Liegen Anfang und Ende der Linie so nah beisammen, ist sie eine geschlossene Runde. */
const isLoop = (shape: CourseShape) => {
  const [ax, ay] = shape.xy[0];
  const [bx, by] = shape.xy[shape.xy.length - 1];
  return Math.hypot(bx - ax, by - ay) <= COURSE_SPAN * 0.03;
};

/**
 * Laufrichtung beim Anteil `fraction`, gemittelt ueber `reach` Kartenpunkte
 * davor und dahinter. `pointAt` kennt nur den einen Abschnitt, in dem die
 * Stelle liegt -- am Anfang einer GPS-Spur oft ein paar Meter Haken, die
 * schraeg zu der Linie stehen, die man auf der Karte sieht. Bei einer
 * geschlossenen Runde reicht das Stueck davor ueber das Ende der Linie.
 */
export function headingAt(shape: CourseShape, fraction: number, reach: number): number {
  const loop = isLoop(shape);
  const span = Math.min(reach, shape.total / 4) / shape.total;
  const at = (f: number) => pointAt(shape, loop ? f - Math.floor(f) : f);
  const a = at(fraction - span);
  const b = at(fraction + span);
  return Math.hypot(b.x - a.x, b.y - a.y) > 0 ? Math.atan2(b.y - a.y, b.x - a.x) : pointAt(shape, fraction).angle;
}

/**
 * Welcher Anteil der Runde einem angetippten Punkt am naechsten liegt.
 *
 * Laeuft die Strecke an einer Stelle zweimal vorbei (hin und zurueck auf
 * demselben Weg), liegen dort zwei Anteile gleich nah. Dann gewinnt der, der
 * naeher an `hint` liegt -- der bisherigen Schaetzung fuer diese Person.
 */
export function nearestFraction(shape: CourseShape, x: number, y: number, hint?: number): number {
  const hits: { fraction: number; distance: number }[] = [];
  for (let i = 1; i < shape.xy.length; i++) {
    const [ax, ay] = shape.xy[i - 1];
    const [bx, by] = shape.xy[i];
    const dx = bx - ax;
    const dy = by - ay;
    const len2 = dx * dx + dy * dy;
    const t = len2 > 0 ? Math.min(1, Math.max(0, ((x - ax) * dx + (y - ay) * dy) / len2)) : 0;
    const distance = Math.hypot(ax + dx * t - x, ay + dy * t - y);
    const along = shape.cum[i - 1] + (shape.cum[i] - shape.cum[i - 1]) * t;
    hits.push({ fraction: along / shape.total, distance });
  }
  const best = Math.min(...hits.map((h) => h.distance));
  // Was hoechstens 3 % der Kartengroesse weiter weg liegt, gilt als gleich nah.
  const near = hits.filter((h) => h.distance <= best + COURSE_SPAN * 0.03);
  const pick =
    hint === undefined
      ? near.reduce((a, b) => (b.distance < a.distance ? b : a))
      : near.reduce((a, b) => (Math.abs(b.fraction - hint) < Math.abs(a.fraction - hint) ? b : a));
  return pick.fraction;
}
